// HELPDOCS1 (docs/build-briefs/HELPDOCS1-guides-and-screen-help.md): the screen-to-section map. Pure, no DOM, so a plain
// node:test file can import it. Given the address of the screen a signed-in person is on (the same hash routes
// screenRoute.ts and consoleRoute.ts publish), it names the guide section that explains the work being done there. A screen the
// map does not know opens the console's guide home. The ids are the `{#id}` headings of src/content/help/<console>/*.md; the
// panel falls back to the top of the guide when a section is missing and to the guide list when a guide is missing, so a
// stale id is never an empty panel.
//
// No screen name is parsed from text and nothing here knows what a person may see: a route is a request to go somewhere and
// the panel only opens the guide for it.

import { parseConsoleRoute } from "./consoleRoute.ts";
import type { HelpTarget } from "./helpPanel.ts";
import { parseRoute, type Route } from "./screenRoute.ts";

/** Where each console's Help opens when the screen has no section of its own. */
export const LOCATION_HELP_HOME: HelpTarget = { slug: "getting-around", section: "start" };
export const FRANCHISOR_HELP_HOME: HelpTarget = { slug: "franchisor-overview", section: "get-around" };

/** A sheet or panel drawn over a location screen that has its own section, found by the test id of its visible root. The first
 * one on screen wins, so the list runs from the most specific sheet to the least. */
export interface HelpProbe {
  readonly testId: string;
  readonly target: HelpTarget;
}

export const LOCATION_HELP_PROBES: readonly HelpProbe[] = [
  { testId: "manager-exceptions-sheet", target: { slug: "closeout", section: "exceptions" } },
  { testId: "cash-drawer-sheet", target: { slug: "closeout", section: "cash-drawer" } },
];

const SETTINGS_SECTIONS: Readonly<Record<string, HelpTarget>> = {
  money: { slug: "settings-money", section: "money-and-pricing" },
  tabsPayment: { slug: "settings-money", section: "tabs-and-payment" },
  recipes: { slug: "price-your-menu", section: "recipes" },
  menu: { slug: "add-inventory", section: "setup" },
  inventory: { slug: "add-inventory", section: "item-editor" },
  purchasing: { slug: "vendors", section: "vendors" },
  discounts: { slug: "settings-money", section: "discounts" },
  corrections: { slug: "settings-money", section: "reasons" },
  people: { slug: "team", section: "add-a-person" },
  operations: { slug: "new-location", section: "location-settings" },
  tablets: { slug: "devices", section: "tablets" },
  connections: { slug: "connections", section: "quickbooks-and-gusto" },
  auditLog: { slug: "connections", section: "audit-log" },
  network: { slug: "connections", section: "billing" },
};

const VIEW_SECTIONS: Readonly<Record<string, HelpTarget>> = {
  stockLevels: { slug: "stock-levels", section: "stock-levels" },
  matchClients: { slug: "handle-the-unusual", section: "match-clients" },
  priceList: { slug: "vendors", section: "price-file" },
  startingStock: { slug: "add-inventory", section: "starting-stock" },
  transfers: { slug: "transfers", section: "send-stock" },
};

const HUB_SECTIONS: Readonly<Record<string, HelpTarget>> = {
  inventory: { slug: "count-stock", section: "overview" },
  ordering: { slug: "order-from-vendors", section: "overview" },
  shift: { slug: "closeout", section: "overview" },
  reports: { slug: "reports", section: "reports-home" },
};

/** The section for a location route. */
export function locationTargetForRoute(route: Route | undefined): HelpTarget {
  if (route === undefined) return LOCATION_HELP_HOME;
  switch (route.kind) {
    case "tabs":
      return { slug: "tabs", section: "open-a-tab" };
    case "ticket":
      return { slug: "ring", section: "add-drinks" };
    case "profile":
      return { slug: "sign-in-and-profile", section: "profile" };
    case "cart":
      return { slug: "order-from-vendors", section: "build-the-cart" };
    case "receive-stock":
      return { slug: "receive-an-order", section: "receive-without-an-order" };
    case "closeout":
      return { slug: "closeout", section: "summary" };
    case "void-history":
      return { slug: "closeout", section: "void-history" };
    case "orders":
      return route.receive === true
        ? { slug: "receive-an-order", section: "receive-against-an-order" }
        : { slug: "order-from-vendors", section: "place-and-approve" };
    case "hub":
      return HUB_SECTIONS[route.hub] ?? LOCATION_HELP_HOME;
    case "settings":
      return SETTINGS_SECTIONS[route.section] ?? LOCATION_HELP_HOME;
    case "view": {
      if (route.view === "reports") {
        return route.report === undefined && route.open === undefined
          ? { slug: "reports", section: "reports-home" }
          : route.report === "custom"
            ? { slug: "reports", section: "custom-reports" }
            : { slug: "reports", section: "read-a-report" };
      }
      if (route.view === "counts") {
        return route.countSessionId === undefined
          ? { slug: "count-stock", section: "start-a-count" }
          : { slug: "count-stock", section: "walk-the-count" };
      }
      return VIEW_SECTIONS[route.view] ?? LOCATION_HELP_HOME;
    }
  }
}

/** The section for the location screen on show: a probe's sheet when one is open, else the route's own section. `visible` says
 * whether the element with a test id is on screen. */
export function locationHelpTarget(hash: string, visible: (testId: string) => boolean): HelpTarget {
  for (const probe of LOCATION_HELP_PROBES) {
    if (visible(probe.testId)) return probe.target;
  }
  return locationTargetForRoute(parseRoute(hash));
}

/** The franchisor console's sections (franchisorMain.ts SECTIONS ids), each with the guide section that explains it. */
export const FRANCHISOR_SECTION_TARGETS: Readonly<Record<string, HelpTarget>> = {
  dashboard: { slug: "franchisor-overview", section: "dashboard" },
  notifications: { slug: "franchisor-overview", section: "notifications" },
  locations: { slug: "franchisor-locations", section: "locations" },
  franchisees: { slug: "franchisor-locations", section: "franchisee-dashboard" },
  catalog: { slug: "franchisor-menu", section: "catalog" },
  recipes: { slug: "franchisor-menu", section: "recipes" },
  publishing: { slug: "franchisor-menu", section: "discounts" },
  brand: { slug: "franchisor-brand", section: "brand" },
  royalty: { slug: "franchisor-billing", section: "billing-and-royalty" },
  "ai-billing": { slug: "franchisor-billing", section: "billing-and-royalty" },
  reports: { slug: "franchisor-reports", section: "reports" },
  "custom-reports": { slug: "franchisor-reports", section: "custom-reports" },
  team: { slug: "franchisor-team", section: "team" },
  audit: { slug: "franchisor-team", section: "audit-log" },
};

/** The section for a franchisor console address (a `#/<section>` hash). */
export function franchisorHelpTarget(hash: string, sections: readonly string[]): HelpTarget {
  // FRBILL3: an old bookmark written as `#royalty` (no slash) is read as `#/royalty`, as the console does.
  const normalized = hash.startsWith("#royalty") ? `#/${hash.slice(1)}` : hash;
  const route = parseConsoleRoute(normalized, sections);
  if (route === undefined) return FRANCHISOR_HELP_HOME;
  return FRANCHISOR_SECTION_TARGETS[route.section] ?? FRANCHISOR_HELP_HOME;
}

/** Snip & Sip Admin: the owner guide (a slug in src/domain/ownerHelp.ts) for each console section, where one exists. A section
 * with no guide opens the owner guide list. Owner guides are never shown in the location or franchisor consoles (DEC-310). */
export const OWNER_SECTION_GUIDES: Readonly<Record<string, string>> = {
  customers: "edit-a-customers-legal-details",
  onboard: "onboard-a-new-customer",
};

export function ownerGuideForSection(section: string | undefined): string | undefined {
  return section === undefined ? undefined : OWNER_SECTION_GUIDES[section];
}
